import firebase from "firebase/app";
import { firestore } from "../firebase";
import DB_PATH from "../DB_PATH";

// Client access to the owned scheduler queue at
// Community/{id}/ScheduledPosts/{id}. These docs hold NO secrets (platforms,
// caption, media, dueAt, status) — tokens are read server-side by the MCP
// poller (mcp/src/repos/scheduledPosts.ts#publishDuePosts), which drains due
// posts across communities. So the admin client can safely read the queue and
// write a scheduled post; the poller does the actual publishing. Firestore
// rules already allow Community subcollection access for admins.

export type SchedulePlatform =
  | "instagram"
  | "tiktok"
  | "linkedin"
  | "facebook"
  | "twitter"
  | "youtube";

export type ScheduledPostStatus =
  | "draft"
  | "scheduled"
  | "publishing"
  | "sent"
  | "partial"
  | "error"
  | "cancelled";

export type ScheduledPost = {
  id: string;
  status: ScheduledPostStatus;
  platforms: string[];
  caption: string;
  dueAt: string | null;
  attempts: number;
  projectId: string | null;
  /** Media attached directly (when not sourced from a composed project). */
  mediaUrls: string[];
  results: Record<string, { via?: string; id?: string; url?: string; error?: string }> | null;
  /**
   * Per-platform engagement pulled by the metrics sweep (Instagram + Facebook
   * first-party today). Numeric fields only: likes, comments, reach, shares.
   */
  metrics: Record<string, Record<string, number>> | null;
};

const postsCollection = (communityId: string) =>
  firestore
    .collection(DB_PATH.COMMUNITY)
    .doc(communityId)
    .collection("ScheduledPosts");

const toIso = (v: any): string | null => {
  if (v && typeof v.toDate === "function") return v.toDate().toISOString();
  return null;
};

// The metrics map carries mixed values (numbers plus via/fetchedAt strings);
// keep only the finite numbers so the UI can safely sum them.
const parseMetrics = (
  raw: any
): Record<string, Record<string, number>> | null => {
  if (!raw || typeof raw !== "object") return null;
  const out: Record<string, Record<string, number>> = {};
  for (const [platform, m] of Object.entries(raw)) {
    if (!m || typeof m !== "object") continue;
    const nums: Record<string, number> = {};
    for (const [k, v] of Object.entries(m as Record<string, unknown>)) {
      if (typeof v === "number" && Number.isFinite(v)) nums[k] = v;
    }
    if (Object.keys(nums).length) out[platform] = nums;
  }
  return Object.keys(out).length ? out : null;
};

const sanitize = (id: string, data: any): ScheduledPost => ({
  id,
  status: (data?.status || "scheduled") as ScheduledPostStatus,
  platforms: Array.isArray(data?.platforms) ? data.platforms : [],
  caption: typeof data?.caption === "string" ? data.caption : "",
  dueAt: toIso(data?.dueAt),
  attempts: typeof data?.attempts === "number" ? data.attempts : 0,
  projectId: typeof data?.projectId === "string" ? data.projectId : null,
  mediaUrls: Array.isArray(data?.mediaUrls) ? data.mediaUrls.filter(Boolean) : [],
  results: data?.results && typeof data.results === "object" ? data.results : null,
  metrics: parseMetrics(data?.metrics),
});

export const listenToScheduledPosts = (
  communityId: string,
  onChange: (posts: ScheduledPost[]) => void,
  onError?: (err: Error) => void
): (() => void) => {
  if (!communityId) {
    onChange([]);
    return () => undefined;
  }
  return postsCollection(communityId)
    .orderBy("dueAt", "desc")
    .limit(50)
    .onSnapshot(
      (snap) => onChange(snap.docs.map((d) => sanitize(d.id, d.data()))),
      (err) => {
        console.error("Error listening to scheduled posts:", err);
        onError?.(err);
      }
    );
};

// Queue a post for the poller. dueAt is an ISO string; a past/now time makes the
// poller pick it up on its next tick. Provide projectId OR mediaUrls.
export const createScheduledPost = async (
  communityId: string,
  input: {
    platforms: SchedulePlatform[];
    caption?: string;
    projectId?: string;
    mediaUrls?: string[];
    dueAt: string;
    via?: "auto" | "firstparty" | "postproxy";
    platformParams?: Record<string, Record<string, unknown>>;
    // Queue as a draft that will not fire until approveScheduledPost.
    asDraft?: boolean;
  }
): Promise<string> => {
  const dueMs = Date.parse(input.dueAt);
  const dueAt = Number.isFinite(dueMs)
    ? firebase.firestore.Timestamp.fromMillis(dueMs)
    : firebase.firestore.Timestamp.now();
  const nowIso = new Date().toISOString();
  const ref = await postsCollection(communityId).add({
    platforms: input.platforms,
    caption: input.caption || "",
    mediaUrls: (input.mediaUrls || []).filter(Boolean),
    projectId: input.projectId || null,
    via: input.via || "auto",
    platformParams: input.platformParams || null,
    status: input.asDraft ? "draft" : "scheduled",
    attempts: 0,
    dueAt,
    createdAt: nowIso,
    updatedAt: nowIso,
  });
  return ref.id;
};

// Edit a queued post before it fires. Only sensible while the post is still
// draft or scheduled; the poller ignores these fields once publishing starts.
// Mirrors the MCP's update_scheduled_post (no schema change).
export const updateScheduledPost = async (
  communityId: string,
  id: string,
  patch: {
    caption?: string;
    dueAt?: string;
    platforms?: SchedulePlatform[];
    mediaUrls?: string[];
    // Set to null to detach from a composed project so mediaUrls takes effect
    // (the publisher uses the project's media whenever projectId is set).
    projectId?: string | null;
  }
): Promise<void> => {
  const update: Record<string, any> = {
    updatedAt: new Date().toISOString(),
  };
  if (typeof patch.caption === "string") update.caption = patch.caption;
  if (Array.isArray(patch.platforms) && patch.platforms.length > 0) {
    update.platforms = patch.platforms;
  }
  if (Array.isArray(patch.mediaUrls)) {
    update.mediaUrls = patch.mediaUrls.map((s) => String(s).trim()).filter(Boolean);
  }
  if (patch.projectId !== undefined) update.projectId = patch.projectId;
  if (typeof patch.dueAt === "string") {
    const dueMs = Date.parse(patch.dueAt);
    if (Number.isFinite(dueMs)) {
      update.dueAt = firebase.firestore.Timestamp.fromMillis(dueMs);
    }
  }
  await postsCollection(communityId).doc(id).set(update, { merge: true });
};

// Approve a draft so the poller will publish it (flip draft -> scheduled).
export const approveScheduledPost = async (
  communityId: string,
  id: string
): Promise<void> => {
  await postsCollection(communityId)
    .doc(id)
    .set(
      { status: "scheduled", updatedAt: new Date().toISOString() },
      { merge: true }
    );
};

// Non-destructive cancel: keep the doc for audit history (matches the MCP).
export const cancelScheduledPost = async (
  communityId: string,
  id: string
): Promise<void> => {
  await postsCollection(communityId)
    .doc(id)
    .set(
      { status: "cancelled", updatedAt: new Date().toISOString() },
      { merge: true }
    );
};

// Bring a cancelled post back as a draft so it re-enters the review queue.
// Restoring to draft (not scheduled) means nothing publishes without a fresh
// approval, even if its old dueAt is in the past.
export const restoreScheduledPost = async (
  communityId: string,
  id: string
): Promise<void> => {
  await postsCollection(communityId)
    .doc(id)
    .set(
      { status: "draft", updatedAt: new Date().toISOString() },
      { merge: true }
    );
};

export const retryScheduledPost = async (
  communityId: string,
  id: string
): Promise<void> => {
  await postsCollection(communityId)
    .doc(id)
    .set(
      {
        status: "scheduled",
        dueAt: firebase.firestore.Timestamp.now(),
        attempts: 0,
        leaseUntil: firebase.firestore.FieldValue.delete(),
        updatedAt: new Date().toISOString(),
      },
      { merge: true }
    );
};
